import React, { useState, useEffect } from "react";
import Navbar from "../components/navbar.jsx";
import Footer from "../components/footer.jsx";

const SERVICES = [
    {
        slug: "hospitals",
        title: "Mental Health Hospitals",
        subtitle: "Comprehensive inpatient & outpatient psychiatric care",
        intro:
            "Our mental health hospitals provide round‑the‑clock care for acute and complex psychiatric needs — staffed by psychiatrists, nurses, and multidisciplinary teams.",
        bullets: [
            "24/7 medically supervised inpatient care",
            "Crisis stabilization and medication management",
            "Structured multidisciplinary rehabilitation programs",
        ],
        highlights: [
            "Acute psychiatric stabilization",
            "Inpatient & day-care programs",
            "Medication & medical monitoring",
            "Family-inclusive discharge planning",
        ],
        details:
            "Nexus hospitals combine safe environments, evidence‑based protocols and compassionate care to support rapid stabilization and continued recovery. Each stay is planned with measurable goals and a clear transition plan to outpatient care.",
        cta: "Contact hospital admissions",
    },
    {
        slug: "child",
        title: "Children & Adolescent Services",
        subtitle: "Developmental, behavioural and therapeutic support for young minds",
        intro:
            "Dedicated teams for children and adolescents delivering assessment, therapy and family support across developmental and emotional concerns.",
        bullets: [
            "Developmental & behavioural assessments",
            "Play therapy, parent training and school‑readiness programs",
            "Specialist autism and ADHD pathways",
        ],
        highlights: [
            "Early intervention and personalised plans",
            "Integrated OT + speech + therapy approach",
            "Parent training & school liaison",
            "Progress tracking and measurable outcomes",
        ],
        details:
            "We focus on practical, measurable progress using short, intensive programs and ongoing parent coaching so gains are maintained and generalised to everyday life.",
        cta: "Book child assessment",
    },
    {
        slug: "development",
        title: "Development Facilities",
        subtitle: "Therapies & programs for developmental delays and special needs",
        intro:
            "Our development services provide structured therapies, habilitation programs and education support for neurodevelopmental conditions.",
        bullets: [
            "Individualised developmental plans",
            "Therapy bundles: OT, speech, physiotherapy and behavioural support",
            "School readiness and inclusive education support",
        ],
        highlights: [
            "Multidisciplinary developmental clinics",
            "Functional goals and ADL training",
            "Therapist-led parent coaching",
            "Transition support to mainstream schooling where appropriate",
        ],
        details:
            "Programs are evidence‑based and intensity‑matched to need — aiming for functional independence, improved learning skills and social participation.",
        cta: "Enquire about development programs",
    },
    {
        slug: "adult",
        title: "Adult Therapy & Counseling",
        subtitle: "Structured psychotherapy for adults across the lifespan",
        intro:
            "Individual, couples and group therapy using evidence‑based approaches to treat anxiety, depression, trauma and life‑stress.",
        bullets: [
            "CBT, DBT, ACT and mindfulness‑based therapies",
            "Couples & family therapy, grief and trauma pathways",
            "Short‑term focused packages and long‑term therapeutic work",
        ],
        highlights: [
            "Goal‑oriented therapy plans",
            "Blended online & in‑person options",
            "Psychotherapy + psychiatric collaboration",
            "Workplace stress and executive coaching",
        ],
        details:
            "Therapy at Nexus blends practical skill building with psychological insight — measurable goals each session and routine reviews to optimise progress.",
        cta: "Start therapy",
    },
    {
        slug: "adultpsychiatrists",
        title: "Adult Psychiatrists",
        subtitle: "Specialist psychiatric care for complex adult mental health",
        intro:
            "Consultant psychiatrists provide diagnostic clarity, medication management and integrated treatment plans for adult mental health conditions.",
        bullets: [
            "Comprehensive psychiatric assessments",
            "Medication optimisation and side‑effect management",
            "Coordination with psychotherapy and rehabilitation services",
        ],
        highlights: [
            "Consultant & senior consultant expertise",
            "Treatment‑resistant case pathways",
            "Electroconvulsive Therapy and neuromodulation referrals",
            "Long‑term follow up and relapse prevention",
        ],
        details:
            "Our psychiatrists take a holistic view — combining medication, therapy and psychosocial interventions to restore functioning and quality of life.",
        cta: "Book psychiatric consultation",
    },
    {
        slug: "testing",
        title: "Psychological & Psychometric Testing",
        subtitle: "Objective assessments to guide diagnosis and treatment",
        intro:
            "Clinically‑validated psychometric tests and neurocognitive batteries administered by trained clinicians to inform personalised care.",
        bullets: [
            "Intelligence & neurocognitive testing",
            "Learning disability screening (dyslexia, dyscalculia)",
            "Disorder-specific tools for ADHD, OCD and mood disorders",
        ],
        highlights: [
            "Standardised, validated instruments",
            "Detailed clinical interpretation & report",
            "Recommendations for therapy, education and workplace adjustments",
            "Follow-up reassessments to track change",
        ],
        details:
            "Assessments help clinicians prescribe the right interventions and measure progress. Reports include clear recommendations for therapy, educational planning and clinical follow-up.",
        cta: "Schedule testing",
    },
    {
        slug: "wellnessprograms",
        title: "Advanced Treatments & Wellness Programs",
        subtitle: "Deep TMS, ECT and specialised wellness pathways",
        intro:
            "A suite of advanced, evidence‑based treatments and wellness programs — for individuals needing options beyond standard therapy.",
        bullets: [
            "Deep Transcranial Magnetic Stimulation (Deep TMS)",
            "Electroconvulsive Therapy (ECT) where clinically indicated",
            "Holistic wellness programs integrating therapy, lifestyle and rehabilitation",
        ],
        highlights: [
            "Neuromodulation and medically supervised procedures",
            "Individualised wellness pathways",
            "Regular monitoring and outcome tracking",
            "Integrated aftercare and rehabilitation",
        ],
        details:
            "Our advanced treatments are carefully selected after specialist assessment. We combine clinical expertise, safety protocols and personalised rehabilitation to ensure the best possible outcomes.",
        cta: "Learn about advanced treatments",
    },
];

function Services() {
    const [activeSection, setActiveSection] = useState(() => {
        const segments = window.location.pathname.split("/").filter(Boolean);
        const slug = segments[1];
        return SERVICES.some((section) => section.slug === slug) ? slug : SERVICES[0].slug;
    });

    useEffect(() => {
        const syncSection = () => {
            const segments = window.location.pathname.split("/").filter(Boolean);
            const slug = segments[1];
            if (SERVICES.some((section) => section.slug === slug)) {
                setActiveSection(slug);
                const el = document.getElementById(slug);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

        syncSection();
        window.addEventListener("popstate", syncSection);
        window.addEventListener("locationchange", syncSection);

        return () => {
            window.removeEventListener("popstate", syncSection);
            window.removeEventListener("locationchange", syncSection);
        };
    }, []);

    const active = SERVICES.find((item) => item.slug === activeSection) || SERVICES[0];

    const handleSectionChange = (slug) => {
        const nextPath = `/services/${slug}`;
        window.history.pushState({}, "", nextPath);
        window.dispatchEvent(new Event("locationchange"));
        setActiveSection(slug);
        const element = document.getElementById(slug);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <>
            <Navbar />
            <main className="relative overflow-hidden bg-slate-950 text-slate-100 pt-24">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.22),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(248,113,113,0.18),_transparent_22%)]" />
                <section className="relative z-10 max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                    <div className="rounded-[2rem] border border-cyan-500/20 bg-slate-900/80 p-6 shadow-2xl backdrop-blur-xl sm:p-10">
                        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] items-center">
                            <div>
                                <p className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-200">
                                    Advanced mental health services built around your journey
                                </p>
                                <h1 className="mt-6 text-4xl font-black tracking-tight text-white sm:text-5xl">
                                    Modern care for assessments, specialised therapy and hospital services.
                                </h1>
                                <p className="mt-5 max-w-2xl text-slate-300 sm:text-lg">
                                    Nexus combines compassionate clinical expertise with evidence-based services to help you move toward clarity, comfort, and recovery.
                                </p>

                                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                                    {SERVICES.map((section) => (
                                        <button
                                            key={section.slug}
                                            onClick={() => handleSectionChange(section.slug)}
                                            className={`rounded-3xl border px-4 py-3 text-left text-sm font-semibold transition ${activeSection === section.slug
                                                    ? "border-cyan-300 bg-cyan-500/15 text-cyan-100 shadow-lg"
                                                    : "border-slate-700 bg-slate-950/80 text-slate-300 hover:border-cyan-300 hover:bg-slate-800/90"
                                                }`}
                                        >
                                            <span className="block text-xs uppercase tracking-[0.24em] text-cyan-300/70">
                                                {section.slug.replace(/-/g, " ")}
                                            </span>
                                            <span className="mt-2 block text-base font-semibold">{section.title}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-6 rounded-[1.75rem] border border-cyan-500/15 bg-slate-950/90 p-6 shadow-xl">
                                <div className="rounded-[1.5rem] bg-gradient-to-br from-cyan-500/10 via-slate-900/70 to-orange-500/10 p-6">
                                    <p className="text-sm uppercase tracking-[0.24em] text-cyan-200">Service spotlight</p>
                                    <h2 className="mt-3 text-2xl font-bold text-white">{active.title}</h2>
                                    <p className="mt-4 text-slate-300">{active.subtitle}</p>
                                </div>
                                <div className="grid gap-3">
                                    {active.bullets.map((item) => (
                                        <div key={item} className="rounded-3xl border border-slate-700/70 bg-slate-900/80 p-4 text-sm text-slate-200">
                                            <div className="flex items-center gap-3">
                                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500/10 text-cyan-300 ring-1 ring-cyan-300/20">✓</span>
                                                <p>{item}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    onClick={() => handleSectionChange(active.slug)}
                                    className="glossy-btn relative inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-white shadow-lg"
                                >
                                    {active.cta}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
                        <aside className="space-y-6 rounded-[2rem] border border-cyan-500/20 bg-slate-900/80 p-6 shadow-2xl">
                            <div className="rounded-3xl border border-cyan-500/20 bg-slate-950/90 p-5">
                                <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Quick links</p>
                                <h2 className="mt-3 text-xl font-semibold text-white">Explore services</h2>
                                <p className="mt-3 text-sm text-slate-400">Jump straight to the service that matters most for your care journey.</p>
                            </div>
                            <nav className="space-y-3">
                                {SERVICES.map((section) => (
                                    <button
                                        key={section.slug}
                                        onClick={() => handleSectionChange(section.slug)}
                                        className={`w-full rounded-2xl px-4 py-3 text-left text-sm font-medium transition ${activeSection === section.slug ? "bg-cyan-500/15 text-cyan-100 ring-1 ring-cyan-300/30" : "bg-slate-950/80 text-slate-300 hover:bg-slate-800/90"
                                            }`}
                                    >
                                        {section.title}
                                    </button>
                                ))}
                            </nav>
                        </aside>

                        <div className="space-y-10">
                            {SERVICES.map((section) => (
                                <section id={section.slug} key={section.slug} className="rounded-[2rem] border border-cyan-500/10 bg-slate-900/85 p-8 shadow-2xl">
                                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                        <div>
                                            <p className="text-sm uppercase tracking-[0.3em] text-cyan-300/70">{section.slug.replace(/-/g, " ")}</p>
                                            <h2 className="mt-2 text-3xl font-bold text-white">{section.title}</h2>
                                            <p className="mt-3 max-w-2xl text-slate-300">{section.intro}</p>
                                        </div>
                                        <span className="inline-flex items-center rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-100">
                                            {section.cta}
                                        </span>
                                    </div>

                                    <div className="mt-8 grid gap-6 lg:grid-cols-2">
                                        <div className="rounded-[1.5rem] border border-slate-700/60 bg-slate-950/80 p-6 text-slate-200">
                                            <h3 className="text-xl font-semibold text-white">What to expect</h3>
                                            <p className="mt-4 leading-relaxed text-slate-300">{section.details}</p>
                                        </div>
                                        <div className="grid gap-3">
                                            {section.highlights.map((feature) => (
                                                <div key={feature} className="rounded-3xl border border-cyan-500/10 bg-slate-950/80 p-5 text-sm text-slate-200 shadow-sm">
                                                    <p className="font-semibold text-white">{feature}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </section>
                            ))}

                            <section className="rounded-[2rem] border border-cyan-500/15 bg-slate-950/85 p-8 shadow-2xl">
                                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                                    <div>
                                        <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Need help choosing?</p>
                                        <h2 className="mt-2 text-3xl font-bold text-white">Start with a guided clinical review</h2>
                                        <p className="mt-4 max-w-2xl text-slate-300">
                                            Our clinical team can help identify which service fits your needs best, then support you with the right next steps.
                                        </p>
                                    </div>
                                    <button className="glossy-btn relative inline-flex items-center justify-center rounded-full px-7 py-4 text-sm font-semibold text-white shadow-lg">
                                        Speak with a specialist
                                    </button>
                                </div>
                                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                                    {[
                                        { label: "Assessment", value: "Data-driven care plans" },
                                        { label: "Comfort", value: "Non-invasive options" },
                                        { label: "Support", value: "Continuous clinical monitoring" },
                                    ].map((item) => (
                                        <div key={item.label} className="rounded-3xl border border-slate-700/60 bg-slate-900/85 p-5 text-slate-200">
                                            <p className="text-sm uppercase tracking-[0.22em] text-cyan-300/70">{item.label}</p>
                                            <p className="mt-3 text-lg font-semibold text-white">{item.value}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export default Services;